/** The ONE case-kind vocabulary (the server's `CASE_KINDS`): what a case reference points at. */
export const CASE_KINDS = ['ticket', 'occurrence', 'dispute', 'safety_incident', 'courier_incident', 'cash_shortfall', 'food_complaint', 'send_claim', 'phone_change', 'widget_case'];
/** The topics only an app-raised record carries (the server's `APP_INTAKE_TOPICS`); the site widget's are `WidgetTopic`. */
export const APP_INTAKE_TOPICS = ['safety', 'fare_dispute', 'occurrence', 'courier_incident', 'cash_shortfall', 'phone_change', 'sign_in'];
export const isAppIntakeTopic = (v) => typeof v === 'string' && APP_INTAKE_TOPICS.includes(v);
/** What happened to a case, as a system entry of the conversation's timeline records it (the server's `CASE_EVENTS`). */
export const CASE_EVENTS = ['attached', 'in_progress', 'answered', 'replied', 'waiting', 'resolved', 'closed', 'reopened', 'escalated', 'decided', 'approved', 'rejected', 'withdrawn'];
const CASE_EVENT_PREFIX = 'case-event:';
/** A system entry's `bodyRef` read back: the event, the kind of record and its id (undefined when it is not a case event). */
export function parseCaseEventBodyRef(bodyRef) {
    if (!bodyRef.startsWith(CASE_EVENT_PREFIX))
        return undefined;
    const [event, kind, ...rest] = bodyRef.slice(CASE_EVENT_PREFIX.length).split(':');
    const recordId = rest.join(':');
    if (!CASE_EVENTS.includes(event ?? '') || !CASE_KINDS.includes(kind ?? '') || recordId === '')
        return undefined;
    return { event: event, kind: kind, recordId };
}
export class CentralCasesApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /**
     * The cases (tickets) of every market the caller covers, or of one covered `market` (filtered on the server, so the page and `hasMore` are
     * that market's; a market the grant does not cover is refused 403). `status`: one status, a comma list, or `ALL`; default the open ones.
     */
    cases(query = {}) {
        const qs = new URLSearchParams();
        if (query.status !== undefined)
            qs.set('status', query.status);
        if (query.market !== undefined)
            qs.set('market', query.market);
        if (query.limit !== undefined)
            qs.set('limit', String(query.limit));
        if (query.offset !== undefined && query.offset > 0)
            qs.set('offset', String(query.offset));
        // D856 (Q6): find a case by its human protocol number (the server forgives case, spaces and zero padding).
        if (query.protocol !== undefined && query.protocol.trim() !== '')
            qs.set('protocol', query.protocol.trim());
        const q = qs.toString();
        return this.client.request({ method: 'GET', path: `/v1/central/cases${q === '' ? '' : `?${q}`}`, auth: true });
    }
    /**
     * The Atendimento of each record (at most 100 ids). `kind: 'ticket'` means a ticket however it was raised (a cash shortfall's, a food
     * complaint, a Send claim, a widget case). A record the caller's grant does not reach at its conversation's market is simply absent.
     */
    caseLinks(kind, ids) {
        return this.client.request({
            method: 'GET', path: `/v1/central/case-links?kind=${encodeURIComponent(kind)}&ids=${ids.map(encodeURIComponent).join(',')}`, auth: true,
        });
    }
}
/** The apps' pre-login help. No account, no cookie: the visitor's session token is returned once and sent as a Bearer. */
export class SignInHelpApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /**
     * Consent (must be `true`) and open an anonymous Atendimento in the rider's or the driver's queue. `marketId` is optional routing (a
     * registered market; an unknown one is refused 400 `WIDGET_MARKET_INVALID`); `countryCode` (ISO alpha-2, the build's country) routes it to that
     * country's agents when the country is served, and is otherwise ignored - nobody is refused for it.
     */
    start(input) {
        return this.client.request({ method: 'POST', path: '/v1/public/sign-in-help/sessions', body: input });
    }
    send(sessionToken, text) {
        return this.client.request({ method: 'POST', path: '/v1/public/sign-in-help/messages', body: { text }, headers: { authorization: `Bearer ${sessionToken}` } });
    }
    messages(sessionToken, after = -1) {
        return this.client.request({ method: 'GET', path: `/v1/public/sign-in-help/messages?after=${encodeURIComponent(String(after))}`, headers: { authorization: `Bearer ${sessionToken}` } });
    }
    end(sessionToken) {
        return this.client.request({ method: 'DELETE', path: '/v1/public/sign-in-help/sessions/current', headers: { authorization: `Bearer ${sessionToken}` }, expect: 'empty' });
    }
}
//# sourceMappingURL=central-cases.js.map